import { Anchor, Breadcrumbs, Card, Grid, Text } from '@mantine/core';
import queryString from 'query-string';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import CouponForm from '../../components/Coupons/CouponForm';
import PartnerAuthorizationAlert from '../../components/Coupons/PartnerAuthorizationAlert';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import { Page } from '../../constants/pageTitles';
import { defaultDiscount } from '../../helpers/discountConfig';
import { generatedCouponLabels } from '../../helpers/platformLabel';
import useFetch from '../../helpers/useFetch';

const page = `${Page['coupon']}`;

const CouponsNewPage = () => {
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();
  const domain = useSelector((state) => state.domain.domain);
  const canGenerateCoupons = Boolean(domain?.capabilities?.generatedCoupons);
  const labels = generatedCouponLabels(domain?.platform);
  const requestedSource = queryString.parse(location.search).source;
  const source = canGenerateCoupons && requestedSource === 'generated' ? 'generated' : 'upload';

  const [coupon] = useState(() =>
    source === 'generated'
      ? { name: 'New_generated_coupon', discount: defaultDiscount() }
      : { name: 'New_dynamic_coupons_list' }
  );

  const [userError, setUserError] = useState();
  const { accountId, domainId } = useParams();

  const listingPath = `/accounts/${accountId}/domains/${domainId}/coupons${canGenerateCoupons ? `?source=${source}` : ''}`;
  const goToListing = () => navigate(listingPath);

  const createGeneratedCoupon = (coupon) =>
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons`, {
      method: 'POST',
      body: JSON.stringify({ name: coupon.name.trim(), source: 'generated', discount: coupon.discount }),
    });

  const uploadCoupon = (coupon, file) => {
    let formData = new FormData();
    formData.append('type', file.type);
    formData.append('file', file);
    formData.append('name', coupon.name.trim());
    return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons`, {
      method: 'POST',
      headers: {},
      body: formData,
    });
  };

  const create = (coupon, file) => {
    setUserError(undefined);
    const request = source === 'generated' ? createGeneratedCoupon(coupon) : uploadCoupon(coupon, file);
    return request.then(goToListing).catch(setUserError);
  };

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#${listingPath}` },
    { title: source === 'generated' ? labels.newCoupon : 'New' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | New`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>
      <Grid>
        <Grid.Col span={12}>
          <Card withBorder>
            <CouponForm coupon={coupon} saveCoupon={create} source={source} onBack={goToListing} />
          </Card>
        </Grid.Col>
      </Grid>
      {userError &&
        (userError.code === 'PARTNER_AUTHORIZATION_REQUIRED' ? (
          <PartnerAuthorizationAlert
            mt="md"
            title="Releva needs permission to create discounts"
            message={userError.message || `Releva is not allowed to create discounts in ${labels.store} yet.`}
            authorizationUrl={userError.authorizationUrl}
          />
        ) : (
          <div style={{ textAlign: 'right' }}>
            <ErrorMessage message={userError.message} />
          </div>
        ))}
    </>
  );
};

export default CouponsNewPage;
